<div class="summary-toolbar">
    <span class="summary-range-label">
        Schedule summary: {{ gridStart() | date: 'mediumDate' }} – {{ gridRangeEnd() | date: 'mediumDate' }}
        ({{ crewScheduleWeekdayCount }} weekdays)
    </span>

    <label class="summary-filter">
        <span>Group by</span>
        <select
            class="form-select form-select-sm summary-filter-select"
            [ngModel]="groupBy()"
            (ngModelChange)="onGroupByChange($event)"
            [disabled]="isLoading()"
        >
            <option value="division">By Division</option>
            <option value="job">By Job</option>
        </select>
    </label>

    <label class="summary-filter">
        <span>Division</span>
        <select
            class="form-select form-select-sm summary-filter-select"
            [ngModel]="selectedDivisionId()"
            (ngModelChange)="onDivisionFilterChange($event)"
            [disabled]="isLoading()"
        >
            <option value="">All Divisions</option>
            @for (division of divisionOptions(); track division.id) {
                <option [value]="division.id">{{ division.name }}</option>
            }
        </select>
    </label>

    <label class="summary-filter">
        <span>Job</span>
        <select
            class="form-select form-select-sm summary-filter-select"
            [ngModel]="selectedJobId()"
            (ngModelChange)="onJobFilterChange($event)"
            [disabled]="isLoading()"
        >
            <option value="">All jobs</option>
            @for (job of activeJobs(); track job.id) {
                <option [value]="normalizeJobId(job.id)">{{ normalizeJobId(job.id) }} — {{ job.name }}</option>
            }
        </select>
    </label>

    <label class="summary-filter">
        <span>Who am I?</span>
        <select
            class="form-select form-select-sm summary-filter-select"
            [ngModel]="selectedMemberHrRef()"
            (ngModelChange)="onMemberFilterChange($event)"
            [disabled]="isLoading()"
        >
            <option [ngValue]="null">Everyone</option>
            @for (member of memberOptions(); track member.hrRef) {
                <option [ngValue]="member.hrRef">{{ member.name }}</option>
            }
        </select>
    </label>

    <button type="button" class="btn btn-outline-secondary btn-sm summary-refresh" (click)="refresh()" [disabled]="isLoading()">
        {{ isLoading() ? 'Loading…' : 'Refresh' }}
    </button>
</div>

@if (isLoading()) {
    <p class="text-muted summary-status">
        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        Loading schedule summary…
    </p>
} @else if (loadError()) {
    <p class="text-danger summary-status">{{ loadError() }}</p>
} @else if (crewRows()?.length) {
    <div class="summary-scroll" appAutoSize [padding]="25" [minHeight]="200" [limitAfterMax]="true">
        <div class="summary-grid" [style.--summary-cols]="gridTemplateColumns()">
            <div class="summary-header-row">
                <div class="summary-name-col summary-header-cell">Crew member</div>
                @for (col of gridColumns(); track col.key) {
                    <div class="summary-day-col summary-header-cell" [class.summary-day-today]="col.isToday">
                        {{ col.label }}
                    </div>
                }
            </div>

            @for (row of crewRows()!; track row.hrRef) {
                <div class="summary-data-row" [class.summary-crew-row-selected]="row.isSelected">
                    <div class="summary-name-col summary-person-name">
                        <span class="summary-foreman-name">{{ row.name }}</span>
                        <span class="text-muted summary-role">{{ row.jobTitle }}</span>
                        @if (row.isForeman) {
                            <span class="summary-foreman-tag">Foreman</span>
                        }
                    </div>
                    @for (cell of row.cells; track cell.key) {
                        <div
                            class="summary-day-col summary-cell"
                            [class.summary-cell-today]="cell.isToday"
                            [class.summary-cell-holiday]="cell.isHoliday && !cell.vpJobId"
                            [class.summary-cell-projected]="!!cell.vpJobId && !cell.isOverEstimate && !cell.isOverHours"
                            [class.summary-cell-over-estimate]="cell.isOverEstimate"
                            [class.summary-cell-over-hours]="cell.isOverHours"
                            [class.summary-cell-lent]="!!cell.lentNote && !!cell.vpJobId"
                            [title]="cellTooltip(cell)"
                        >
                            @if (cell.vpJobId) {
                                <span class="summary-job">{{ cell.vpJobId }}</span>
                                <span class="summary-phase">{{ cell.phase }}</span>
                                @if (cell.lentNote) {
                                    <span class="summary-lent-note">{{ cell.lentNote }}</span>
                                }
                            } @else if (cell.isHoliday) {
                                <span class="summary-holiday-badge">HOL</span>
                            }

                            @if (cell.offMembers?.length) {
                                <div class="summary-off-strip" [title]="offStripTooltip(cell)">
                                    @for (m of cell.offMembers!.slice(0, 4); track m.hrRef) {
                                        <span
                                            class="summary-off-chip"
                                            [class.summary-off-approved]="m.status === 'Approved'"
                                            [class.summary-off-pending]="m.status === 'Pending'"
                                        >{{ m.initials }}</span>
                                    }
                                    @if (cell.offMembers!.length > 4) {
                                        <span class="summary-off-chip summary-off-more">+{{ cell.offMembers!.length - 4 }}</span>
                                    }
                                </div>
                            }
                        </div>
                    }
                </div>
            }
        </div>
    </div>
} @else if (crewRows()) {
    <p class="text-muted summary-status">No crew found for the selected division.</p>
} @else if (!hasVisibleForemen() && groupBy() !== 'job') {
    <p class="text-muted summary-status">
        @if (selectedDivisionId()) {
            No foremen found for the selected division.
        } @else if (selectedJobId()) {
            No crews are scheduled on the selected job.
        } @else {
            No scheduled work found.
        }
    </p>
} @else {
    <div class="summary-scroll" appAutoSize [padding]="25" [minHeight]="300" [limitAfterMax]="true">
        <div class="summary-grid" [style.--summary-cols]="gridTemplateColumns()">
            <div class="summary-header-row">
                <div class="summary-name-col summary-header-cell">Foreman</div>
                @for (col of gridColumns(); track col.key) {
                    <div class="summary-day-col summary-header-cell" [class.summary-day-today]="col.isToday">
                        {{ col.label }}
                    </div>
                }
            </div>

            @for (group of foremanGroups(); track group.key) {
                <div
                    class="summary-division-header"
                    [class.summary-division-header-empty]="!group.rows.length && !!group.groupJobId"
                >
                    <span class="summary-division-header-sticky">
                        <span class="summary-division-header-label">{{ group.label }}</span>
                        <span class="summary-division-header-count">({{ group.rows.length }})</span>
                    </span>
                </div>
                @if (!group.rows.length) {
                    <div class="summary-empty-group-row">
                        <span class="summary-empty-group-note">No crews scheduled</span>
                    </div>
                }
                @for (row of group.rows; track row.hrRef) {
                <div class="summary-data-row">
                    <div class="summary-name-col summary-foreman-name-col">
                        <span class="summary-foreman-name">{{ row.name }}</span>
                        <span class="text-muted summary-role">{{ row.jobTitle }}</span>
                    </div>
                    @for (cell of row.cells; track cell.key) {
                        <div
                            class="summary-day-col summary-cell"
                            [class.summary-cell-today]="cell.isToday"
                            [class.summary-cell-holiday]="cell.isHoliday && !cell.vpJobId"
                            [class.summary-cell-projected]="!!cell.vpJobId && !cell.isOverEstimate && !cell.isOverHours"
                            [class.summary-cell-over-estimate]="cell.isOverEstimate"
                            [class.summary-cell-over-hours]="cell.isOverHours"
                            [title]="cellTooltip(cell)"
                        >
                            @if (cell.vpJobId) {
                                <span class="summary-job">{{ cell.vpJobId }}</span>
                                <span class="summary-phase">{{ cell.phase }}</span>
                            } @else if (cell.isHoliday) {
                                <span class="summary-holiday-badge">HOL</span>
                            }

                            @if (cell.lentOut.length || cell.borrowedIn.length) {
                                <div class="summary-chip-strip">
                                    @for (chip of cell.borrowedIn; track chip.label) {
                                        <span class="summary-chip summary-chip-in" [title]="chip.tooltip">{{ chip.label }}</span>
                                    }
                                    @for (chip of cell.lentOut; track chip.label) {
                                        <span class="summary-chip summary-chip-out" [title]="chip.tooltip">{{ chip.label }}</span>
                                    }
                                </div>
                            }

                            @if (cell.offMembers?.length) {
                                <div class="summary-off-strip" [title]="offStripTooltip(cell)">
                                    @for (m of cell.offMembers!.slice(0, 4); track m.hrRef) {
                                        <span
                                            class="summary-off-chip"
                                            [class.summary-off-approved]="m.status === 'Approved'"
                                            [class.summary-off-pending]="m.status === 'Pending'"
                                        >{{ m.initials }}</span>
                                    }
                                    @if (cell.offMembers!.length > 4) {
                                        <span class="summary-off-chip summary-off-more">+{{ cell.offMembers!.length - 4 }}</span>
                                    }
                                </div>
                            }
                        </div>
                    }
                </div>
                }
            }
        </div>
    </div>
}
